import { createSlice } from "@reduxjs/toolkit";
import { mapValues, identity, get, isEmpty, upperCase } from "lodash-es";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useReducer } from "react";
import Radio from "backbone.radio";

import { store } from "common/store";
import { CLOSE_EXPORT_DIALOG, OPEN_EXPORT_DIALOG } from "common/actions/downloads";
import { CheckBox } from "common/components/checkbox";
import { Modal } from "common/components/modal";
import { OptionsButton } from "common/components/buttons/optionsButton";
import { PrimaryButton, CancelButton } from "common/components/buttons";
import { SCHEDULE_AUTOMATIC_DOWNLOAD } from "common/reducers/downloads";
import { captureException } from "extensions/sentry";
import { emitter } from "common/emitter";
import { request } from "common/components/request";
import { toast } from "extensions/toast";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/pro-light-svg-icons";

const submitExportRequest = function(payload) {
    const { automaticDownload, ..._payload } = {
        filters: payload.filters || {},
        ...payload
    };
    
    // At this point we can convert qualifiers to encoded string
    if (Object.prototype.hasOwnProperty.call(_payload.filters, "q")) {
        _payload.filters.q = _payload.filters.q.toString(true);
    }
    
    request("/merchant/:merchant_ref/user/export", {
        method: "POST",
        data: _payload
    }).then((data) => {
        if (automaticDownload) {
            store.dispatch(SCHEDULE_AUTOMATIC_DOWNLOAD(data.job_ref));
        }
        
        const showNotifications = (e) => {
            e.preventDefault();
            emitter.emit("open.downloadsPanel");
        };
        toast.success((
            <div>Your file will appear in the <strong><FontAwesomeIcon icon={faBell}/> <a className="alert-link" onClick={showNotifications} href="#">notifications panel</a></strong> when ready for download</div>
        ));
    }).catch((error) => {
        toast.error("Unable to generate download.");
        captureException(error);
    });
};

function ExportContent({
    content,
    activeContent,
    filename,
    resources,
    activeResource,
    formats,
    activeFormat,
    columns,
    overrides,
    activeOverrides,
    activeColumns,
    hasAutomaticDownload,
    automaticDownload,
    ON_CHANGE,
    ON_RESOURCE_CHANGE,
    ON_CLOSE,
    ON_EXPORT
}) {
    const onChange = (key) => (val) => ON_CHANGE([key, val]);
    return (
        <div className="configurable-table-export table-export">
            {filename && <Filename filename={filename} setFilename={onChange("filename")} />}
            {formats && <Formats formats={formats} activeFormat={activeFormat} setActiveFormat={onChange("activeFormat")}/>}
            {resources && <Resources resources={resources} activeResource={activeResource} setActiveResource={ON_RESOURCE_CHANGE}/>}
            {overrides && <PickList className="overrides" choices={overrides} activeChoices={activeOverrides || []} setActiveChoices={onChange("activeOverrides")} />}
            {content && <PickList choices={content} activeChoices={activeContent} setActiveChoices={onChange("activeContent")} hasSelectAll />}
            {columns && <PickList choices={columns} activeChoices={activeColumns} setActiveChoices={onChange("activeColumns")} hasSelectAll />}
            {hasAutomaticDownload && (
            <CheckBox
                label="Automatically download when ready"
                value={automaticDownload}
                onChange={() => ON_CHANGE(["automaticDownload", !automaticDownload])}
                name="automaticDownload"
            />
            )}
            <ExportActions onClose={ON_CLOSE} onExport={ON_EXPORT}/>
        </div>
    );
}

// Programmatically show export dialog the old way.
export const showExportDialog = ({
    filters = {},
    resources,
    activeResource,
    content,
    activeContent,
    dialogTitle = "Export",
    exporter,
    filename = "",
    formats,
    activeFormat,
    automaticDownload,
    overrides,
    activeOverrides
}) => {
    const hasValues = content.length === 1 && get(content[0], "structure");

    const getExportDetails = (state) => {
        const _details = {
            name: state.filename,
            content: getExportContent(state),
            content_type: state.activeFormat,
            resource: state.activeResource,
            overrides: state.activeOverrides,
            filters // TODO MATT: maybe `|| {}`
        };
        return _details;
    };

    const mapGetterValues = () => {
        if (!hasValues) return [];
        const getters = Radio.channel("main").request("get:getters");
        const structure = get(content[0], "structure");
        return (structure.transactions_values || structure.values).map((c) => {
            let getter;
            if (Array.isArray(c)) {
                getter = getters.findById(c[0]);
            } else {
                getter = getters.findById(c);
            }
            
            if (!getter) {
                throw new Error(`Unknown getter with id: ${getter}`);
            }
            
            return {
                value: getter._id,
                label: getter.name
            };
        });
    };

    const columns = mapGetterValues();
    const activeColumns = columns.map(({ value }) => value);

    const getExportContent = (state) => {
        if (state.activeResource === "users_management") {
            return state.activeContent.reduce((acc, c) => {
                const { value_payload } = content.find(({ value }) => value === c);
                acc[c] = value_payload;
                
                return acc;
            }, Object.create(null));
        }
        
        const normalizationDict = {
            settlement: "settlements",
            sales_refunds: "settlements",
            chargeback: "chargebacks",
            authorization: "authorizations",
            fee: "fees"
        };
        
        let filteredContent = state.activeContent;
        // Remove net_cash_flow from transactions export if it's there
        if (state.activeResource === "transactions") {
            filteredContent = state.activeContent.filter((c) => c !== "net_cash_flow");
        }
        
        return filteredContent.map((c) => {
            const matchContentStructure = content.find(({ value }) => value === c).structure;
            
            let matchContent;
            // In reconciliation export we pass both summaries and transaction values to the export dialog
            if (state.activeResource === "summaries") {
                matchContent = matchContentStructure.summaries_values;
            } else if (state.activeResource === "transactions") {
                matchContent = matchContentStructure.transactions_values;
            } else if (matchContentStructure) {
                matchContent = matchContentStructure.values;
            }
            
            let structure = null;
            if (matchContent) {
                structure = {
                    values: matchContent.reduce((memo, cur) => {
                        let getter = cur;
                        let currencyGetter = false;
                        if (Array.isArray(cur)) {
                            getter = cur[0];
                            currencyGetter = cur[1];
                        }
                        
                        if (!hasValues || state.activeColumns.includes(getter)) {
                            memo.push(getter);
                            
                            if (currencyGetter) {
                                memo.push(currencyGetter);
                            }
                        }
                        
                        return memo;
                    }, [])
                };
                
                if (state.activeResource === "pivot") {
                    structure = {
                        rows: matchContentStructure.rows,
                        filters: matchContentStructure.filters,
                        columns: matchContentStructure.columns,
                        values: structure.values
                    };
                }
            }
            
            return {
                value: normalizationDict[c] || c,
                structure
            };
        });
    };
    
    // When a requested resource changes, the available options to export must change also.
    const ON_RESOURCE_CHANGE = (state, { payload }) => ({
        ...state,
        activeResource: payload,
        content: payload === "transactions" ? content.filter(({ value }) => value !== "net_cash_flow") : content,
        activeContent: payload === "transactions" ? state.activeContent.filter((value) => value !== "net_cash_flow") : state.activeContent
    });

    const close = () => store.dispatch(CLOSE_EXPORT_DIALOG());
    
    const onExport = (state) => {
        const payload = { ...getExportDetails(state), automaticDownload: state.automaticDownload };
        if (exporter) {
            exporter(payload, submitExportRequest);
        } else {
            submitExportRequest(payload);
        }
        close();
    };
    
    store.dispatch(OPEN_EXPORT_DIALOG(
        {
            dialogTitle,
            initialState: {
                content,
                activeContent: activeContent || [get(content, 0)?.value].filter(identity),
                filename,
                columns,
                activeColumns,
                resources,
                activeResource,
                formats,
                activeFormat,
                automaticDownload,
                overrides,
                activeOverrides
            },
            extraReducers: {
                ON_RESOURCE_CHANGE
            },
            close,
            exporter: onExport
        }
    ));
};

const usePropsOrReduxConfiguration = (props) => {
    const show = useSelector((state) => state.downloads.showExportDialog);
    const configuration = useSelector((state) => state.downloads.exportDialogConfiguration);
    
    if (props && !isEmpty(props)) return props;
    return { show, ...configuration };
};

/*
  The docBuilder export endpoint is the default exporter function used.
  It takes as an argument:

  export type PayloadType = {|
      name: string,
      filters: {|
          start?: string,
          end?: string,
          sort?: string,
          q?: Qualifiers,
          datetype?: "activity" | "settlement",
          summary_refs?: Array<string>
      |},
      content_type: "csv" | "xlsx" | "jpeg" | "png" | "svg" | "pdf";
      content: Array<{| value: ExportContentType, structure: ?ExportStructureType |}>,
      resource: "mids" | "transactions" | "summaries" | "pivot";
      currency?: string
  |};

  By default the `prepareExport` mappings above are used to populate some of these arguments from the state of the export dialog.
  The rest are expected to be mapped with a provided `mapStateToExport` function.

  In some cases it is desirable to not use the docBuilder export endpoint, but some other export function.
  Then, an `exporter` function is expected, taking as an argument the state of the export dialog.
  If the `exporter` function is provided, the `mapStateToExport` is ignored.
  */

export function ExportDialog(props) {
    const {
        show,
        close,
        initialState,
        extraReducers,
        exporter,
        dialogTitle
    } = usePropsOrReduxConfiguration(props);

    const defaultReducer = {
        ON_CHANGE: (currentState, { payload: [key, value] }) => ({ ...currentState, [key]: value }),
        RESET: (currentState, { payload: newState }) => newState
    };
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const { reducer, actions } = useMemo(() => createSlice({ name: "ExportDialog", reducers: { ...defaultReducer, ...extraReducers }, initialState }), [extraReducers, initialState]);
    const [state, dispatch] = useReducer(reducer, initialState);
    const boundActions = useMemo(() => mapValues(actions, (a) => (...args) => dispatch(a(...args))), [actions]);
    
    // This is to get around the fact that `useReducer` doesn't reset when initialState changes.
    // When initialState changes, we want the reducer to use that value as the current state.
    useEffect(() => {
        boundActions.RESET(initialState);
    }, [boundActions, initialState]);
    
    return (
        <Modal
            close={close}
            header={dialogTitle}
            modal
            portalId="modal-export"
            show={show}
            $zIndex={1031} // Must appear over preferences overlay
        >
            <ExportContent
                {...state}
                {...boundActions}
                ON_CLOSE={close}
                ON_EXPORT={() => (exporter ? exporter(state) : submitExportRequest(state))}
            />
        </Modal>
    );
}

function Filename({ filename, setFilename }) {
    return (
        <div className="file-name">
            <div className="field-name">File name</div>
            <input
                type="text"
                className="form-control fileName"
                name="fileName"
                value={filename}
                onChange={(e) => setFilename(e.target.value.replace(/ /gi, "-"))}
            />
        </div>
    );
}

function Resources({ resources, activeResource, setActiveResource }) {
    return resources.length > 1 && (
    <div key="content" className="format-options">
        <div className="field-name">Content</div>
        <OptionsButton structure={resources} activeValue={activeResource} onChange={setActiveResource} />
    </div>
    );
}

function Formats({ formats, activeFormat, setActiveFormat }) {
    return (
        <div key="file_format" className="format-options">
            <div className="field-name">File format</div>
            <OptionsButton
                structure={formats.map((f) => ({ value: f, label: upperCase(f) }))}
                activeValue={activeFormat}
                onChange={setActiveFormat}
            />
        </div>
    );
}

function PickList({
    className = "content-options", choices, activeChoices, setActiveChoices, hasSelectAll
}) {
    if (!choices || (hasSelectAll && choices.length < 2)) return null;
    
    const allSelected = activeChoices.length === choices.length;

    return (
        <div className={className}>
            {hasSelectAll && (
                <div className="bottom-bordered">
                    <CheckBox label="Select all" value={allSelected} onChange={() => setActiveChoices(allSelected ? [] : choices.map(({ value }) => value))} name="selectAllSections" />
                </div>
            )}
            <div className="options bottom-bordered">
                {choices.map(({ value, label }) => {
                    const included = activeChoices.includes(value);
                    return (
                        <CheckBox
                            key={value}
                            label={label}
                            onChange={() => setActiveChoices(included ? activeChoices.filter((v) => v !== value) : activeChoices.concat(value))}
                            name={value}
                            value={included}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function ExportActions({ onClose, onExport }) {
    return (
        <div className="actions">
            <div className="group" />
            <div className="group">
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <PrimaryButton onClick={onExport}>Export</PrimaryButton>
            </div>
        </div>
    );
}
